import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { compose, branch, renderNothing } from 'recompose';
import { connect } from 'react-redux';
import ReactTable from 'react-table';
import {
  Button,
} from 'react-bootstrap';

import {
  getEndDateApiFormat, getEquipment, getStartDateApiFormat,
} from '../selectors';
import { queryGetEquipmentUsageDataGQL } from '../graphql/GetTimeForEquipmentQuery';
import { defaultTableFiltering, startsWithTableFiltering, openInNewTab } from '../../../utils';
import NewTabLink from '../../../components/NewTabLink';
import { getTicketLink } from '../../ticketViewer/links';
import '../../timesheetApprovals/components/TimesheetDetails.css';
import { parseDateApiFormat } from '../../../utils/parseDateFormats';

const UNAVAILABLE = 'unavailable';

const propTypes = exact({
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  equipmentId: PropTypes.number,

  loading: PropTypes.bool.isRequired,
  allEquipmentUsage: PropTypes.array.isRequired,
});

const handleViewTimeEntry = (paramUserId, weekOfDate) => {
  const baseUrl = '/submissions/timesheets/';

  const formattedApiDate = parseDateApiFormat(weekOfDate);
  const timesheetSubmissionUrl = `${baseUrl}?userId=${paramUserId}&originDate=${formattedApiDate}&timeEntryDate=${formattedApiDate}`;
  openInNewTab(timesheetSubmissionUrl);
};

const getTicketState = (ticketNumber) => {
  if (!ticketNumber) {
    return null;
  }
  if (!ticketNumber.ticket) {
    return 'unavailable';
  }
  return ticketNumber.ticket.state;
};

const renderTicketNumber = (ticketNumber, isClickable) => {
  if (isClickable) {
    const number = (ticketNumber) ? ticketNumber.number : '-';
    let ticketId = '';
    let jobId = '';

    if (ticketNumber && ticketNumber.ticket) {
      ticketId = ticketNumber.ticket.id;
      jobId = ticketNumber.ticket.job.id;
    }
    const url = getTicketLink(ticketId, jobId);
    return (
      <NewTabLink to={url}>
        {number}
      </NewTabLink>
    );
  }

  return (ticketNumber);
};

const DataTable = (props) => {
  const {
    loading,
    allEquipmentUsage,
  } = props;

  const noData = () => {
    if (loading) {
      return null;
    }
    return <div className="rt-noData">No rows found</div>;
  };

  return (
    <React.Fragment>
      <ReactTable
        data={allEquipmentUsage}
        loading={loading}
        NoDataComponent={noData}
        filterable
        defaultFilterMethod={(filter, row) => {
          return defaultTableFiltering(filter, row);
        }}
        columns={[
          {
            Header: 'First Name',
            id: 'firstname',
            accessor: (e) => { return e.timesheet.user.firstname; },
          },
          {
            Header: 'Last Name',
            id: 'lastname',
            accessor: (e) => { return e.timesheet.user.lastname; },
          },
          {
            Header: 'Ticket Number',
            id: 'ticketNumber',
            accessor: (e) => { return e.ticketNumber; },
            Cell: (row) => {
              const { ticketNumber } = row.original;
              const ticketState = getTicketState(ticketNumber);
              const isClickable = (ticketNumber && ticketState !== UNAVAILABLE);

              return (
                <div className={`summary-details-${ticketState}`}>
                  {renderTicketNumber(ticketNumber, isClickable)}
                </div>
              );
            },
          },
          {
            Header: 'Date',
            id: 'date',
            accessor: (e) => { return e.date.display; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'Equipment Number',
            id: 'number',
            accessor: (e) => { return e.equipment; },
            Cell: (row) => {
              const { equipment } = row.original;
              const number = (equipment) ? equipment.equipmentNumber : '-';
              return (
                <div>{number}</div>
              );
            },
          },
          {
            Header: 'Actions',
            id: 'actions',
            filterable: false,
            sortable: false,
            Cell: (row) => {
              const { date, timesheet: { user } } = row.original;
              return (
                <div>
                  <Button
                    bsStyle="info"
                    bsSize="xsmall"
                    onClick={() => {
                      handleViewTimeEntry(user.id, date.display);
                    }}
                  >View Entry
                  </Button>
                </div>
              );
            },
          },
        ]}
        defaultPageSize={20}
        className="-striped -highlight"
      />
    </React.Fragment>
  );
};


DataTable.propTypes = propTypes;

const withNoFilterValues = branch(
  (props) => {
    const { startDate, endDate, equipmentId } = props;
    return !startDate || !endDate || !equipmentId;
  },
  renderNothing,
);

const mapStateToProps = (state) => {
  const equipment = getEquipment(state);
  return {
    startDate: getStartDateApiFormat(state),
    endDate: getEndDateApiFormat(state),
    equipmentId: (equipment) ? equipment.id : null,
  };
};

const withRedux = connect(mapStateToProps, {});

const enhance = compose(
  withRedux,
  withNoFilterValues,
  queryGetEquipmentUsageDataGQL,
);

export default enhance(DataTable);
